import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import DeviceSettingsCore from '../components/deviceSettings/DeviceSettingsCore';
import '../components/deviceSettings/deviceSettingsSurfaces.css';
import { functionDefs } from '../particleFunctions';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isGlobalRole, canRestartControllerForDevice } from '../utils/roles';
import { getCalibrationSecondsLeft } from '../utils/calibrationCountdown';
import { buildClimateThresholdState, serializeClimateThresholds } from '../utils/climateThresholds';
import {
  RELAY_PENDING_TIMEOUT_MS,
  RELAY_SOCKET_MAP,
  RELAY_MODE_MANUAL_ON,
  RELAY_MODE_MANUAL_OFF,
  normalizeRelayMode,
  normalizeRelayMap,
} from '../utils/deviceRelay';

function formatDateTime(value, locale, timeZone) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'medium',
      hour12: false,
      timeZone,
    }).format(date);
  } catch (_) {
    return date.toLocaleString(locale, { hour12: false });
  }
}

function buildPageThresholdState(dev) {
  if (!dev) return buildClimateThresholdState({});
  if (dev.controller === 'water') return buildWaterThresholdState(dev.thresholds || {});
  return buildClimateThresholdState(dev.thresholds);
}

function parseWaterThresholdValue(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { min: raw.min ?? '', max: raw.max ?? '' };
  }
  if (Array.isArray(raw)) {
    return { min: raw[0] ?? '', max: raw[1] ?? '' };
  }
  if (typeof raw === 'string') {
    const parts = raw
      .split(/\s*-\s*|,|;/)
      .map((part) => part.trim())
      .filter(Boolean);
    return { min: parts[0] ?? '', max: parts[1] ?? '' };
  }
  if (typeof raw === 'number') {
    return { min: '', max: String(raw) };
  }
  return { min: '', max: '' };
}

function serializeWaterThresholdValue(value) {
  const min = String(value?.min ?? '').trim();
  const max = String(value?.max ?? '').trim();
  if (!min && !max) return null;
  if (!min) return max;
  if (!max) return min;
  return `${min}-${max}`;
}

function buildWaterThresholdState(thresholds = {}) {
  return {
    ph: parseWaterThresholdValue(thresholds.ph),
    ec: parseWaterThresholdValue(thresholds.ec),
    orp: parseWaterThresholdValue(thresholds.orp),
    temp: parseWaterThresholdValue(thresholds.temp),
    level: parseWaterThresholdValue(thresholds.level),
  };
}

function serializeWaterThresholds(thresholdState = {}) {
  const payload = {
    ph: serializeWaterThresholdValue(thresholdState.ph),
    ec: serializeWaterThresholdValue(thresholdState.ec),
    orp: serializeWaterThresholdValue(thresholdState.orp),
    temp: serializeWaterThresholdValue(thresholdState.temp),
    level: serializeWaterThresholdValue(thresholdState.level),
  };
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value));
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function buildWaterConfigState(config = {}) {
  return {
    tankMaxHeight: firstDefined(config.tankMaxHeight, ''),
    tankFullVolume: firstDefined(config.tankFullVolume, ''),
    dMin: firstDefined(config.dMin, config.displayInterval, ''),
    sMin: firstDefined(config.sMin, config.serverInterval, ''),
  };
}

function cleanWaterConfig(config = {}) {
  return Object.fromEntries(
    Object.entries(config).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
  );
}

function toUnderscoreName(s) {
  return s ? String(s).replace(/\s+/g, '_') : '';
}

export default function DeviceSettingsPage({ embedded = false, deviceId = null }) {
  const params = useParams();
  const id = deviceId || params.id;
  const { t, lang } = useLang();
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  if (!token) return embedded ? null : <Navigate to="/login" replace />;

  const decode = (tok) => {
    if (!tok) return {};
    try {
      const d = JSON.parse(atob(tok.split('.')[1]));
      return { role: d.role };
    } catch {
      return {};
    }
  };
  const role = user?.role || decode(token).role;
  const isAdmin = isGlobalRole(role);
  const isSuperuser = isGlobalRole(role);

  const [device, setDevice] = useState(null);
  const [config, setConfig] = useState(null);
  const [displayInterval, setDisplayIntervalVal] = useState('');
  const [serverInterval, setServerIntervalVal] = useState('');
  const [active, setActive] = useState('');
  const [values, setValues] = useState({});
  const [disabled, setDisabled] = useState({});
  const [msg, setMsg] = useState(null);
  const [wipe, setWipe] = useState(true);
  const [systemTime, setSystemTime] = useState(null);
  const [timezone, setTimezone] = useState('Asia/Jerusalem');
  const [timezoneDraft, setTimezoneDraft] = useState('Asia/Jerusalem');
  const [co2Offset, setCo2Offset] = useState(0);
  const [co2OffsetDraft, setCo2OffsetDraft] = useState('0');
  const [calibrationTargetPpm, setCalibrationTargetPpm] = useState('400');
  const [controllerTime, setControllerTime] = useState(null);
  const [manualTimestamp, setManualTimestamp] = useState('');
  const [timeActionPending, setTimeActionPending] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [roomSaving, setRoomSaving] = useState(false);
  const [unbindSaving, setUnbindSaving] = useState(false);
  const [calibrationCountdownSec, setCalibrationCountdownSec] = useState(0);
  const [calibrationStartedUi, setCalibrationStartedUi] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  const [powerAlerts, setPowerAlerts] = useState(true);
  const [thresholdAlerts, setThresholdAlerts] = useState(true);
  const [trendAlerts, setTrendAlerts] = useState(false);
  const [initialAlertSettings, setInitialAlertSettings] = useState({
    telegramEnabled: true,
    powerAlerts: true,
    thresholdAlerts: true,
    trendAlerts: false,
  });
  const [settingsThresholds, setSettingsThresholds] = useState(() => buildClimateThresholdState({}));
  const [initialSettingsThresholds, setInitialSettingsThresholds] = useState(() =>
    buildClimateThresholdState({})
  );
  const [settingsWaterConfig, setSettingsWaterConfig] = useState(() => buildWaterConfigState({}));
  const [initialSettingsWaterConfig, setInitialSettingsWaterConfig] = useState(() => buildWaterConfigState({}));
  const [deviceDisplayName, setDeviceDisplayName] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState(null);
  const [settingsProgressText, setSettingsProgressText] = useState('');
  const [notificationDelivery, setNotificationDelivery] = useState(null);
  const [notificationActionPending, setNotificationActionPending] = useState(false);
  const [relayPending, setRelayPending] = useState({});
  const [relayCommandStatus, setRelayCommandStatus] = useState(null);
  const [relayExpected, setRelayExpected] = useState({});
  const [relayModes, setRelayModes] = useState({
    ac: 'auto',
    dryer: 'auto',
    co2: 'auto',
    extra: 'auto',
  });
  const [nameSaving, setNameSaving] = useState(false);
  const [restartPending, setRestartPending] = useState(false);
  const [restartStatus, setRestartStatus] = useState(null);
  const relayPendingTimersRef = useRef({});
  const restartStatusTimerRef = useRef(null);

  const toDisplay = (s) => (s ? s.replace(/_/g, ' ') : s);
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const locale =
    lang === 'he' ? 'he-IL' : lang === 'ua' ? 'uk-UA' : lang === 'es' ? 'es-ES' : 'en-US';
  const tx = (key) => t(key);

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const loadDevice = useCallback(() => {
    authFetch(`/api/devices/${id}`, {}, { token, onUnauthorized: handleUnauthorized })
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => {
        if (!d) return;
        setDevice(d);
        setNotificationDelivery(d.notificationDelivery || null);
        setSelectedRoomId(d.roomId ? String(d.roomId) : '');
        setControllerTime(d.controllerTime || d.lastUpdated || null);
      })
      .catch((err) => console.error('Failed to load device', err));
  }, [handleUnauthorized, id, token]);

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadDevice();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [loadDevice]);

  const pendingCalibration = Boolean(device?.pendingCalibration);
  const calibrationCountdownText = `${Math.floor(calibrationCountdownSec / 60)
    .toString()
    .padStart(2, '0')}:${(calibrationCountdownSec % 60).toString().padStart(2, '0')}`;

  useEffect(() => {
    const requestedAt = device?.calibrationRequestedAt;
    const initialLeft = getCalibrationSecondsLeft(pendingCalibration, requestedAt);
    if (initialLeft <= 0) {
      setCalibrationStartedUi(false);
      setCalibrationCountdownSec(0);
      return undefined;
    }
    let timerId;
    const tick = () => {
      const left = getCalibrationSecondsLeft(pendingCalibration, requestedAt);
      if (left <= 0) {
        setCalibrationStartedUi(false);
        setCalibrationCountdownSec(0);
        if (timerId != null) window.clearInterval(timerId);
        return;
      }
      setCalibrationStartedUi(true);
      setCalibrationCountdownSec(left);
    };
    tick();
    timerId = window.setInterval(tick, 1000);
    return () => {
      if (timerId != null) window.clearInterval(timerId);
    };
  }, [pendingCalibration, device?.calibrationRequestedAt]);

  useEffect(() => {
    if (!device) return;
    const nextAlertSettings = {
      telegramEnabled: device.telegramEnabled ?? device.alertsEnabled !== false,
      powerAlerts: device.powerAlerts ?? device.notifyOnOffline ?? true,
      thresholdAlerts: device.thresholdAlerts ?? device.alertsEnabled !== false,
      trendAlerts: device.trendAlerts ?? device.trendAlertsEnabled ?? false,
    };
    setTelegramEnabled(nextAlertSettings.telegramEnabled);
    setPowerAlerts(nextAlertSettings.powerAlerts);
    setThresholdAlerts(nextAlertSettings.thresholdAlerts);
    setTrendAlerts(nextAlertSettings.trendAlerts);
    setInitialAlertSettings(nextAlertSettings);
    setNotificationDelivery(device.notificationDelivery || null);
    const next = buildPageThresholdState(device);
    setSettingsThresholds(next);
    setInitialSettingsThresholds(next);
    if (device.controller === 'water') {
      const nextWaterConfig = buildWaterConfigState(device.config || {});
      setSettingsWaterConfig(nextWaterConfig);
      setInitialSettingsWaterConfig(nextWaterConfig);
    }
    setDeviceDisplayName(toDisplay(device.customName || device.name || ''));
    setRelayModes((prev) => ({
      ...prev,
      ...(device?.meta?.relayModes?.ac != null ? { ac: normalizeRelayMode(device.meta.relayModes.ac) } : {}),
      ...(device?.meta?.relayModes?.dryer != null ? { dryer: normalizeRelayMode(device.meta.relayModes.dryer) } : {}),
      ...(device?.meta?.relayModes?.co2 != null ? { co2: normalizeRelayMode(device.meta.relayModes.co2) } : {}),
      ...(device?.meta?.relayModes?.extra != null ? { extra: normalizeRelayMode(device.meta.relayModes.extra) } : {}),
    }));
  }, [device]);

  useEffect(() => {
    setRelayPending({});
    setRelayExpected({});
    setRelayCommandStatus(null);
    Object.values(relayPendingTimersRef.current).forEach(clearTimeout);
    relayPendingTimersRef.current = {};
  }, [id]);

  useEffect(
    () => () => {
      Object.values(relayPendingTimersRef.current).forEach(clearTimeout);
      if (restartStatusTimerRef.current) window.clearTimeout(restartStatusTimerRef.current);
    },
    []
  );

  const farmFetchOpts = useMemo(
    () => (device?.farmId ? { farmContextId: String(device.farmId) } : {}),
    [device?.farmId]
  );

  const hasThresholdChanges = useMemo(() => {
    const hasAlertChanges =
      telegramEnabled !== initialAlertSettings.telegramEnabled ||
      powerAlerts !== initialAlertSettings.powerAlerts ||
      thresholdAlerts !== initialAlertSettings.thresholdAlerts ||
      trendAlerts !== initialAlertSettings.trendAlerts;
    if (device?.controller === 'water') {
      return (
        JSON.stringify(settingsThresholds) !== JSON.stringify(initialSettingsThresholds) ||
        JSON.stringify(settingsWaterConfig) !== JSON.stringify(initialSettingsWaterConfig) ||
        hasAlertChanges
      );
    }
    return (
      JSON.stringify(serializeClimateThresholds(settingsThresholds)) !==
        JSON.stringify(serializeClimateThresholds(initialSettingsThresholds)) ||
      hasAlertChanges
    );
  }, [
    device?.controller,
    settingsThresholds,
    initialSettingsThresholds,
    settingsWaterConfig,
    initialSettingsWaterConfig,
    telegramEnabled,
    powerAlerts,
    thresholdAlerts,
    trendAlerts,
    initialAlertSettings,
  ]);
  const latestRelayStatuses = useMemo(() => normalizeRelayMap(device || {}), [device]);

  useEffect(() => {
    if (!Object.keys(relayExpected).length) return undefined;
    const intervalId = window.setInterval(() => {
      loadDevice();
    }, 1500);
    return () => window.clearInterval(intervalId);
  }, [loadDevice, relayExpected]);

  useEffect(() => {
    const confirmedRelayKeys = Object.entries(relayExpected)
      .filter(([relayKey, expectedValue]) => {
        const current = Number(latestRelayStatuses?.[relayKey]);
        if (!Number.isFinite(current)) return false;
        return expectedValue === 'any' ? true : current === Number(expectedValue);
      })
      .map(([relayKey]) => relayKey);
    if (!confirmedRelayKeys.length) return;
    confirmedRelayKeys.forEach((relayKey) => {
      if (relayPendingTimersRef.current[relayKey]) {
        clearTimeout(relayPendingTimersRef.current[relayKey]);
        delete relayPendingTimersRef.current[relayKey];
      }
    });
    setRelayPending((prev) => {
      const next = { ...prev };
      confirmedRelayKeys.forEach((relayKey) => {
        next[relayKey] = false;
      });
      return next;
    });
    setRelayExpected((prev) => {
      const next = { ...prev };
      confirmedRelayKeys.forEach((relayKey) => {
        delete next[relayKey];
      });
      return next;
    });
    setRelayCommandStatus(null);
  }, [latestRelayStatuses, relayExpected]);

  const setThresholdField = useCallback(
    (...args) => {
      if (device?.controller === 'water') {
        const [key, part, value] = args;
        setSettingsThresholds((prev) => ({
          ...prev,
          [key]: { ...(prev[key] || {}), [part]: value },
        }));
      } else {
        const [section, metric, part, value] = args;
        setSettingsThresholds((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [metric]: {
              ...prev[section]?.[metric],
              [part]: value,
            },
          },
        }));
      }
    },
    [device?.controller]
  );

  const setWaterConfigField = useCallback((key, value) => {
    setSettingsWaterConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveDisplayName = useCallback(async () => {
    const trimmed = toUnderscoreName(deviceDisplayName);
    if (!trimmed) {
      setMsg({ type: 'error', text: t('saveFailed') });
      return;
    }
    setNameSaving(true);
    try {
      const res = await authFetch(
        `/api/device/${encodeURIComponent(id)}/name`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        },
        { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || t('saveFailed') });
        return;
      }
      setMsg({ type: 'success', text: t('settingsSavedSuccess') });
      loadDevice();
    } catch {
      setMsg({ type: 'error', text: t('saveFailed') });
    } finally {
      setNameSaving(false);
    }
  }, [deviceDisplayName, id, token, handleUnauthorized, farmFetchOpts, loadDevice, t]);

  const saveThresholdsOnly = useCallback(async () => {
    if (!device) {
      setSettingsStatus({ type: 'warning', text: t('saveFailed') });
      return;
    }
    if (!hasThresholdChanges) return;
    setSavingSettings(true);
    setSettingsStatus(null);
    setSettingsProgressText(t('settingsProgressSavingDb'));
    try {
      if (device.controller === 'water') {
        const waterThresholdsChanged =
          JSON.stringify(settingsThresholds) !== JSON.stringify(initialSettingsThresholds);
        const waterConfigChanged =
          JSON.stringify(settingsWaterConfig) !== JSON.stringify(initialSettingsWaterConfig);
        const cleanedThresholds = waterThresholdsChanged ? serializeWaterThresholds(settingsThresholds) : {};
        const cleanedConfig = waterConfigChanged ? cleanWaterConfig(settingsWaterConfig) : {};
        const response = await authFetch(
          `/api/device/${encodeURIComponent(id)}/settings`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              thresholds: cleanedThresholds,
              config: cleanedConfig,
              telegramEnabled,
              powerAlerts,
              thresholdAlerts,
              trendAlerts,
            }),
          },
          { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
        );
        const responseData = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(responseData?.error || 'settings');

        const nextThresholdState = waterThresholdsChanged
          ? buildWaterThresholdState(responseData?.thresholds || cleanedThresholds)
          : settingsThresholds;
        const nextWaterConfig = waterConfigChanged
          ? buildWaterConfigState(responseData?.controllerConfig || cleanedConfig)
          : settingsWaterConfig;
        setSettingsThresholds(nextThresholdState);
        setInitialSettingsThresholds(nextThresholdState);
        setSettingsWaterConfig(nextWaterConfig);
        setInitialSettingsWaterConfig(nextWaterConfig);
        setInitialAlertSettings({ telegramEnabled, powerAlerts, thresholdAlerts, trendAlerts });
        setSettingsStatus({ type: 'success', text: t('settingsSavedSuccess') });
        setMsg({ type: 'success', text: t('settingsSavedSuccess') });
        loadDevice();
        return;
      }

      const climateThresholdsChanged =
        JSON.stringify(serializeClimateThresholds(settingsThresholds)) !==
        JSON.stringify(serializeClimateThresholds(initialSettingsThresholds));
      const cleanedThresholds = climateThresholdsChanged ? serializeClimateThresholds(settingsThresholds) : {};
      const response = await authFetch(
        `/api/device/${encodeURIComponent(id)}/settings`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            thresholds: cleanedThresholds,
            telegramEnabled,
            powerAlerts,
            thresholdAlerts,
            trendAlerts,
          }),
        },
        { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
      );
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(responseData?.error || 'settings');
      setNotificationDelivery(responseData.notificationDelivery || notificationDelivery);

      const fallbackRequestedThresholds = {
        tempDay: cleanedThresholds.temp_day,
        humDay: cleanedThresholds.hum_day,
        co2Day: cleanedThresholds.co2_day,
        tempNight: cleanedThresholds.temp_night,
        humNight: cleanedThresholds.hum_night,
        co2Night: cleanedThresholds.co2_night,
      };
      const requestedThresholds =
        responseData?.requestedThresholds && typeof responseData.requestedThresholds === 'object'
          ? responseData.requestedThresholds
          : fallbackRequestedThresholds;
      const nextThresholdState = climateThresholdsChanged
        ? buildClimateThresholdState(requestedThresholds)
        : settingsThresholds;
      setSettingsThresholds(nextThresholdState);
      setInitialSettingsThresholds(nextThresholdState);
      setInitialAlertSettings({ telegramEnabled, powerAlerts, thresholdAlerts, trendAlerts });
      setSettingsStatus({ type: 'success', text: t('deviceCard.climateParamsUpdated') });
      setMsg({ type: 'success', text: t('deviceCard.climateParamsUpdated') });
      loadDevice();
    } catch (err) {
      const errorText =
        err?.message && err.message !== 'settings'
          ? `${t('settingsApplyFailedPrefix')}: ${err.message}`
          : t('saveFailed');
      setSettingsStatus({ type: 'error', text: errorText });
      setMsg({ type: 'error', text: errorText });
    } finally {
      setSavingSettings(false);
      setSettingsProgressText('');
    }
  }, [
    device,
    hasThresholdChanges,
    id,
    token,
    handleUnauthorized,
    farmFetchOpts,
    settingsThresholds,
    initialSettingsThresholds,
    settingsWaterConfig,
    initialSettingsWaterConfig,
    telegramEnabled,
    powerAlerts,
    thresholdAlerts,
    trendAlerts,
    loadDevice,
    notificationDelivery,
    t,
  ]);

  const enableClimateNotificationsForMe = useCallback(async () => {
    if (!device) return;
    setNotificationActionPending(true);
    setSettingsStatus(null);
    try {
      const response = await authFetch(
        `/api/device/${encodeURIComponent(id)}/notification-delivery/climate/enable`,
        { method: 'POST' },
        { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'settings');
      setNotificationDelivery(data.notificationDelivery || null);
      setDevice((prev) =>
        prev ? { ...prev, notificationDelivery: data.notificationDelivery || null } : prev
      );
      setSettingsStatus({ type: 'success', text: t('deviceSettingsModal.climateAlertsEnabledForMe') });
      loadDevice();
    } catch (err) {
      const errorText =
        err?.message && err.message !== 'settings'
          ? `${t('settingsApplyFailedPrefix')}: ${err.message}`
          : t('deviceSettingsModal.climateAlertsEnableFailed');
      setSettingsStatus({ type: 'error', text: errorText });
    } finally {
      setNotificationActionPending(false);
    }
  }, [device, farmFetchOpts, handleUnauthorized, id, loadDevice, t, token]);

  const handleRelayCommand = useCallback(
    async (relayKey, mode) => {
      const socketNumber = RELAY_SOCKET_MAP[relayKey];
      if (!socketNumber) return;
      const normalizedMode = normalizeRelayMode(mode);
      const initialExpectedState =
        normalizedMode === RELAY_MODE_MANUAL_ON
          ? 1
          : normalizedMode === RELAY_MODE_MANUAL_OFF
            ? 0
            : null;
      setRelayPending((prev) => ({ ...prev, [relayKey]: true }));
      setRelayExpected((prev) => ({
        ...prev,
        ...(initialExpectedState == null ? {} : { [relayKey]: initialExpectedState }),
      }));
      setRelayModes((prev) => ({ ...prev, [relayKey]: normalizedMode }));
      setRelayCommandStatus(null);
      if (relayPendingTimersRef.current[relayKey]) {
        clearTimeout(relayPendingTimersRef.current[relayKey]);
      }
      relayPendingTimersRef.current[relayKey] = setTimeout(() => {
        setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
        setRelayExpected((prev) => {
          const next = { ...prev };
          delete next[relayKey];
          return next;
        });
        setRelayCommandStatus({
          type: 'danger',
          text: t('deviceCard.relayCommandTimeout'),
        });
        loadDevice();
        delete relayPendingTimersRef.current[relayKey];
      }, RELAY_PENDING_TIMEOUT_MS);
      console.info('[UI][RelayCommand][SettingsPage] click', {
        deviceId: id,
        relayKey,
        requestedMode: mode,
        normalizedMode,
        socketNumber,
        expectedState: initialExpectedState,
      });
      try {
        const response = await authFetch(
          `/api/device/${encodeURIComponent(id)}/relay-command`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              socket: socketNumber,
              mode: normalizedMode,
            }),
          },
          { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
        );
        const data = await response.json().catch(() => ({}));
        console.info('[UI][RelayCommand][SettingsPage] api-response', {
          deviceId: id,
          relayKey,
          normalizedMode,
          socketNumber,
          httpStatus: response.status,
          ok: response.ok,
          body: data,
        });
        if (!response.ok || data?.ok === false) {
          throw new Error(data?.error || 'toggleSocket');
        }
        if (data?.pendingTelemetryConfirmation) {
          const telemetryExpectedState =
            normalizedMode === RELAY_MODE_MANUAL_ON
              ? 1
              : normalizedMode === RELAY_MODE_MANUAL_OFF
                ? 0
                : Number.isFinite(Number(data?.autoExpectedState))
                  ? Number(data.autoExpectedState)
                  : undefined;
          setRelayExpected((prev) => ({
            ...prev,
            ...(telemetryExpectedState === undefined ? {} : { [relayKey]: telemetryExpectedState }),
          }));
        } else if (normalizedMode === RELAY_MODE_MANUAL_ON || normalizedMode === RELAY_MODE_MANUAL_OFF) {
          if (relayPendingTimersRef.current[relayKey]) {
            clearTimeout(relayPendingTimersRef.current[relayKey]);
            delete relayPendingTimersRef.current[relayKey];
          }
          setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
          setRelayExpected((prev) => {
            const next = { ...prev };
            delete next[relayKey];
            return next;
          });
          const modeLabel =
            normalizedMode === RELAY_MODE_MANUAL_ON
              ? t('deviceSettingsModal.modeManualOn')
              : t('deviceSettingsModal.modeManualOff');
          setRelayCommandStatus({
            type: 'success',
            text: `${String(relayKey || '').toUpperCase()}: ${t('deviceCard.modeLabel')} ${modeLabel}`,
          });
        } else {
          if (relayPendingTimersRef.current[relayKey]) {
            clearTimeout(relayPendingTimersRef.current[relayKey]);
            delete relayPendingTimersRef.current[relayKey];
          }
          setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
          setRelayExpected((prev) => {
            const next = { ...prev };
            delete next[relayKey];
            return next;
          });
          setRelayCommandStatus({
            type: 'success',
            text: `${String(relayKey || '').toUpperCase()}: ${t('deviceCard.modeLabel')} ${t('deviceSettingsModal.modeAuto')}`,
          });
        }
        loadDevice();
      } catch (err) {
        console.error('[UI][RelayCommand][SettingsPage] failed', {
          deviceId: id,
          relayKey,
          normalizedMode,
          socketNumber,
          error: err?.message || err,
        });
        if (relayPendingTimersRef.current[relayKey]) {
          clearTimeout(relayPendingTimersRef.current[relayKey]);
          delete relayPendingTimersRef.current[relayKey];
        }
        setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
        setRelayExpected((prev) => {
          const next = { ...prev };
          delete next[relayKey];
          return next;
        });
        const text = err?.message && err.message !== 'toggleSocket' ? err.message : t('commandError');
        setRelayCommandStatus({ type: 'danger', text });
      }
    },
    [id, token, handleUnauthorized, farmFetchOpts, loadDevice, t]
  );

  const restartController = useCallback(async () => {
    setRestartPending(true);
    setRestartStatus(null);
    if (restartStatusTimerRef.current) {
      window.clearTimeout(restartStatusTimerRef.current);
      restartStatusTimerRef.current = null;
    }
    try {
      const response = await authFetch(
        `/api/device/${encodeURIComponent(id)}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ functionName: 'restartDevice', arg: '' }),
        },
        { token, onUnauthorized: handleUnauthorized, ...farmFetchOpts }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || Number(data?.return_value) !== 1) {
        const err = new Error(data?.error || 'restartDevice');
        err.code = data?.code;
        err.status = response.status;
        throw err;
      }
      const acceptedText = t('deviceSettingsModal.restartAccepted');
      setRestartStatus({ type: 'success', text: acceptedText });
      setMsg({ type: 'success', text: acceptedText });
      restartStatusTimerRef.current = window.setTimeout(() => {
        setRestartStatus(null);
        restartStatusTimerRef.current = null;
      }, 90000);
    } catch (err) {
      const notReachable =
        err?.code === 'DEVICE_NOT_REACHABLE' || err?.status === 503;
      const text = notReachable
        ? t('deviceSettingsModal.restartNotReachable')
        : t('deviceSettingsModal.restartFailed');
      setRestartStatus({ type: 'danger', text });
      setMsg({ type: 'error', text });
    } finally {
      setRestartPending(false);
    }
  }, [id, token, handleUnauthorized, farmFetchOpts, t]);

  const refreshSystemTime = useCallback(() => {
    authFetch('/api/system/time', {}, { token, onUnauthorized: handleUnauthorized })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setSystemTime(data);
        setTimezone(data.timezone || 'Asia/Jerusalem');
        setTimezoneDraft(data.timezone || 'Asia/Jerusalem');
        const nextCo2Offset = Number.isFinite(Number(data.co2Offset)) ? Number(data.co2Offset) : 0;
        setCo2Offset(nextCo2Offset);
        setCo2OffsetDraft(String(nextCo2Offset));
      })
      .catch((err) => console.error('Failed to load system time', err));
  }, [handleUnauthorized, token]);

  useEffect(() => {
    refreshSystemTime();
  }, [refreshSystemTime]);

  useEffect(() => {
    if (!token || !device?.farmId) {
      setRooms([]);
      return;
    }
    const fid = String(device.farmId);
    authFetch(
      `/api/rooms?farmId=${encodeURIComponent(fid)}`,
      {},
      { token, onUnauthorized: handleUnauthorized, farmContextId: fid }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setRooms(Array.isArray(list) ? list : []))
      .catch(() => setRooms([]));
  }, [token, device?.farmId, handleUnauthorized]);

  const saveRoomAssignment = async () => {
    setRoomSaving(true);
    try {
      const res = await authFetch(
        `/api/device/${encodeURIComponent(id)}/room`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId: selectedRoomId || null }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || t('saveFailed') });
        return;
      }
      setMsg({ type: 'success', text: t('settingsSavedSuccess') });
      const refreshed = await authFetch(`/api/devices/${id}`, {}, { token, onUnauthorized: handleUnauthorized });
      if (refreshed.ok) {
        const d = await refreshed.json();
        setDevice(d);
        setSelectedRoomId(d.roomId ? String(d.roomId) : '');
      }
    } catch {
      setMsg({ type: 'error', text: t('saveFailed') });
    } finally {
      setRoomSaving(false);
    }
  };

  const unbindFromFarm = async () => {
    if (!window.confirm(t('deviceAssignment.releaseConfirm'))) return;
    setUnbindSaving(true);
    try {
      const adminId = device?.internalId || id;
      const res = await authFetch(
        `/api/admin/devices/${encodeURIComponent(adminId)}/farm`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ farmId: null }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || t('deviceAssignment.releaseError') });
        return;
      }
      window.dispatchEvent(new Event('shefa-unassigned-refresh'));
      setMsg({ type: 'success', text: t('deviceAssignment.releaseDone') });
      navigate('/devices');
    } catch {
      setMsg({ type: 'error', text: t('deviceAssignment.releaseError') });
    } finally {
      setUnbindSaving(false);
    }
  };

  const refreshConfig = () => {
    authFetch(`/api/device/${id}/config`, {}, { token, onUnauthorized: handleUnauthorized })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cfg) {
          setConfig(cfg);
          if (cfg.dMin !== undefined) setDisplayIntervalVal(cfg.dMin);
          if (cfg.sMin !== undefined) setServerIntervalVal(cfg.sMin);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    refreshConfig();
  }, [id, token]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      refreshSystemTime();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refreshSystemTime]);

  const saveDisplay = async () => {
    const val = parseFloat(displayInterval);
    if (!(val > 0 && val <= 1440)) {
      setMsg({ type: 'error', text: t('intervalError') });
      return;
    }
    const res = await authFetch(
      `/api/device/${id}/callFunction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ functionName: 'setDisplayInterval', arg: String(val) }),
      },
      { token, onUnauthorized: handleUnauthorized }
    );
    if (res.ok) setConfig((c) => ({ ...(c || {}), dMin: val }));
  };

  const saveServer = async () => {
    const val = parseFloat(serverInterval);
    if (!(val > 0 && val <= 1440)) {
      setMsg({ type: 'error', text: t('intervalError') });
      return;
    }
    const res = await authFetch(
      `/api/device/${id}/callFunction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ functionName: 'setServerInterval', arg: String(val) }),
      },
      { token, onUnauthorized: handleUnauthorized }
    );
    if (res.ok) setConfig((c) => ({ ...(c || {}), sMin: val }));
  };

  const simpleCall = async (fn, arg = '') => {
    await authFetch(
      `/api/device/${id}/callFunction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ functionName: fn, arg }),
      },
      { token, onUnauthorized: handleUnauthorized }
    );
  };

  const syncControllerTimeNow = async () => {
    setTimeActionPending(true);
    try {
      const res = await authFetch(
        `/api/device/${id}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ functionName: 'syncTimeNow', arg: timezone }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json();
      if (!res.ok || Number(data?.return_value) !== 1) {
        throw new Error('sync');
      }
      setMsg({ type: 'success', text: tx('commandSuccess') });
      refreshConfig();
      refreshSystemTime();
    } catch (err) {
      console.error('Failed to sync controller time', err);
      setMsg({ type: 'error', text: tx('commandError') });
    } finally {
      setTimeActionPending(false);
    }
  };

  const saveTimezone = async () => {
    setTimeActionPending(true);
    try {
      const parsedCo2Offset = Number(co2OffsetDraft);
      if (!Number.isFinite(parsedCo2Offset)) {
        throw new Error('co2_offset');
      }
      const tzRes = await authFetch(
        '/api/system/timezone',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            timezone: timezoneDraft,
            co2Offset: Math.trunc(parsedCo2Offset),
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const tzData = await tzRes.json();
      if (!tzRes.ok) {
        throw new Error(tzData?.error || 'timezone');
      }

      setTimezone(tzData.timezone);
      setTimezoneDraft(tzData.timezone);
      const nextCo2Offset = Number.isFinite(Number(tzData.co2Offset)) ? Number(tzData.co2Offset) : 0;
      setCo2Offset(nextCo2Offset);
      setCo2OffsetDraft(String(nextCo2Offset));
      await authFetch(
        `/api/device/${id}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ functionName: 'setTimezone', arg: tzData.timezone }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      setMsg({ type: 'success', text: tx('save') });
      refreshSystemTime();
      refreshConfig();
    } catch (err) {
      console.error('Failed to save timezone', err);
      setMsg({ type: 'error', text: String(err?.message || tx('commandError')) });
    } finally {
      setTimeActionPending(false);
    }
  };

  const forceCalibrateFromCore = async (targetPpmRaw) => {
    setTimeActionPending(true);
    try {
      const targetPpm = Number(targetPpmRaw);
      if (!Number.isFinite(targetPpm)) {
        throw new Error('calibration-target');
      }
      const res = await authFetch(
        `/api/device/${id}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            functionName: 'calibrateCO2ppm',
            arg: String(Math.trunc(targetPpm)),
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json();
      if (!res.ok || (data?.return_value != null && Number(data.return_value) !== 1)) {
        throw new Error('forceCalibrate');
      }
      setMsg({ type: 'success', text: tx('commandSuccess') });
      await loadDevice();
    } catch (err) {
      console.error('Failed to force calibrate sensor', err);
      setMsg({ type: 'error', text: tx('commandError') });
    } finally {
      setTimeActionPending(false);
    }
  };

  const setManualControllerTime = async () => {
    if (!manualTimestamp) {
      setMsg({ type: 'error', text: tx('commandError') });
      return;
    }

    const unixTs = Math.floor(new Date(manualTimestamp).getTime() / 1000);
    if (!Number.isFinite(unixTs)) {
      setMsg({ type: 'error', text: tx('commandError') });
      return;
    }

    setTimeActionPending(true);
    try {
      const res = await authFetch(
        `/api/device/${id}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            functionName: 'setManualTime',
            arg: `${unixTs},${timezone}`,
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      const data = await res.json();
      if (!res.ok || Number(data?.return_value) !== 1) {
        throw new Error('manual-time');
      }
      setControllerTime(new Date(unixTs * 1000).toISOString());
      setMsg({ type: 'success', text: tx('commandSuccess') });
      refreshConfig();
    } catch (err) {
      console.error('Failed to set manual controller time', err);
      setMsg({ type: 'error', text: tx('commandError') });
    } finally {
      setTimeActionPending(false);
    }
  };

  const availableFunctions = (device?.functions || []).filter((fn) =>
    functionDefs.some((d) => d.name === fn)
  );

  const vpdAnalysisAllowed =
    !device?.farmPlan || device.farmPlan.features?.vpdAnalysis === true;
  const canRestartController = canRestartControllerForDevice(user, device);

  const defFor = (name) => functionDefs.find((d) => d.name === name);

  const handleChange = (name, key, val) => {
    setValues((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), [key]: val } }));
  };

  const submit = (name, extra = {}) => {
    const def = defFor(name);
    if (!def) return;
    if (name === 'setVPDType' && !vpdAnalysisAllowed) {
      return;
    }
    if (name === 'getReadings') {
      return;
    }
    const args = def.args
      .map((a) => {
        if (typeof a === 'string') return values[name]?.[a] || '';
        return values[name]?.[a.key] || '';
      })
      .concat(Object.values(extra));
    const argStr = args.filter((x) => x !== '').join(',');
    setDisabled((prev) => ({ ...prev, [name]: true }));
    authFetch(
      `/api/device/${id}/callFunction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ functionName: name, arg: argStr })
      },
      { token, onUnauthorized: handleUnauthorized }
    )
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok && res.status === 401) {
          setMsg({ type: 'error', text: t('particleAuthError') });
          return;
        }
        const rv = parseInt(data.return_value, 10);
        if (rv === 1) {
          setMsg({ type: 'success', text: t('commandSuccess') });
        } else {
          setMsg({ type: 'error', text: t('commandError') });
        }
      })
      .catch((err) => {
        console.error('Failed to call function', err);
        setMsg({ type: 'error', text: t('commandError') });
      })
      .finally(() => {
        setTimeout(() =>
          setDisabled((prev) => ({ ...prev, [name]: false })), 1000);
        setTimeout(() => setMsg(null), 3000);
      });
  };

  const renderForm = (name) => {
    const def = defFor(name);
    if (!def) return null;
    if (def.args.length === 0) return null;
    return (
      <div className="mt-2">
        {def.args.map((arg, idx) => {
          if (typeof arg === 'string') {
            return (
              <input
                key={idx}
                type="number"
                className="form-control mb-2"
                placeholder={t(`args.${arg}`)}
                value={values[name]?.[arg] || ''}
                onChange={(e) => handleChange(name, arg, e.target.value)}
              />
            );
          }
          if (arg.type === 'text') {
            return (
              <input
                key={idx}
                type="text"
                className="form-control mb-2"
                placeholder={t(`args.${arg.key}`)}
                value={values[name]?.[arg.key] || ''}
                onChange={(e) => handleChange(name, arg.key, e.target.value)}
              />
            );
          }
          if (arg.enum) {
            return (
              <select
                key={idx}
                className="form-select mb-2"
                value={values[name]?.[arg.key] || arg.enum[0]}
                onChange={(e) => handleChange(name, arg.key, e.target.value)}
              >
                {arg.enum.map((opt) => (
                  <option key={opt} value={opt}>
                    {t(`enums.${opt}`)}
                  </option>
                ))}
              </select>
            );
          }
          return null;
        })}
        {(() => {
          const h = t(`hints.${name}`);
          return h && h !== `hints.${name}` ? (
            <div className="form-text mb-2">{h}</div>
          ) : null;
        })()}
        <button
          className="btn btn-success"
          onClick={() => submit(name)}
        >
          {t('args.submit')}
        </button>
      </div>
    );
  };

  const serverDate = systemTime?.serverTime ? new Date(systemTime.serverTime) : null;
  const controllerDate = controllerTime ? new Date(controllerTime) : null;
  const driftSeconds =
    serverDate && controllerDate
      ? Math.abs(serverDate.getTime() - controllerDate.getTime()) / 1000
      : null;
  const driftTone =
    driftSeconds == null ? 'secondary' : driftSeconds <= 2 ? 'success' : 'warning';

  return (
    <div
      className={`device-settings-page ${embedded ? 'device-settings-page--embedded' : 'container mt-3'}`}
      dir={dir}
    >
      {!embedded ? (
        <div className="mb-3">
          <LanguageSelector />
        </div>
      ) : null}
      <div className="device-settings-surface p-3 p-md-4 mb-4">
        {!embedded ? (
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <a href={`/device/${id}`} className="btn btn-link px-0">
              &larr; {t('backToController')}
            </a>
            <Link to="/devices" className="btn btn-outline-secondary" style={{ borderRadius: 12 }}>
              {t('backToControllers')}
            </Link>
          </div>
        ) : null}
        <h1 className="mb-1 h3 fw-bold">{t('deviceSettingsBrandTitle')}</h1>
        <p className="text-muted mb-3">
          {t('deviceSettingsBrandSubtitle', id)}
          {device ? ` · ${toDisplay(device.customName || device.name) || ''}` : ''}
        </p>
        {msg && (
          <div
            className={`alert py-2 px-3 mb-3 ${
              msg.type === 'success' ? 'alert-success' : 'alert-danger'
            }`}
            style={{ borderRadius: 12 }}
          >
            {msg.text}
          </div>
        )}
        {device?.farmId ? (
          <div className="device-settings-modal__section mb-3">
            <h6 className="fw-bold mb-2">{t('deviceAssignment.roomLabel')}</h6>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 280, borderRadius: 12 }}
                value={selectedRoomId}
                onChange={(e) => setSelectedRoomId(e.target.value)}
              >
                <option value="">{t('deviceAssignment.roomNone')}</option>
                {rooms.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ borderRadius: 12 }}
                disabled={roomSaving}
                onClick={saveRoomAssignment}
              >
                {roomSaving ? t('loading') : t('deviceAssignment.roomSave')}
              </button>
            </div>
          </div>
        ) : null}
        {isSuperuser && device?.farmId ? (
          <div className="device-settings-modal__section mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ borderRadius: 12 }}
              disabled={unbindSaving}
              onClick={unbindFromFarm}
            >
              {unbindSaving ? t('loading') : t('deviceAssignment.releaseDevice')}
            </button>
            <div className="form-text">{t('deviceAssignment.releaseHint')}</div>
          </div>
        ) : null}
        {config && (
          <div className="device-settings-modal__section mb-3">
            <h6 className="fw-bold">{t('config')}</h6>
            <div className="mb-2">
              {t('displayInterval')}: {config.dMin} | {t('serverInterval')}: {config.sMin}
            </div>
            {isAdmin && (
              <div className="mb-2">
                <input
                  type="number"
                  step="0.1"
                  className="form-control w-auto d-inline me-2"
                  value={displayInterval}
                  onChange={(e) => setDisplayIntervalVal(e.target.value)}
                />
                <button className="btn btn-sm btn-primary me-3" onClick={saveDisplay}>
                  {t('save')}
                </button>
                <input
                  type="number"
                  step="0.1"
                  className="form-control w-auto d-inline me-2"
                  value={serverInterval}
                  onChange={(e) => setServerIntervalVal(e.target.value)}
                />
                <button className="btn btn-sm btn-primary" onClick={saveServer}>
                  {t('save')}
                </button>
              </div>
            )}
            <div className="mb-2">
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={refreshConfig}>
                {t('refreshConfig')}
              </button>
              {isAdmin && (
                <>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => simpleCall('saveConfig')}
                  >
                    {t('saveConfig')}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => simpleCall('loadConfig')}
                  >
                    {t('loadConfig')}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => simpleCall('softReset')}
                  >
                    {t('softReset')}
                  </button>
                </>
              )}
            </div>
            {isAdmin && (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="wipe"
                  checked={wipe}
                  onChange={(e) => setWipe(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="wipe">
                  {t('wipeEEPROM')}
                </label>
                <button
                  className="btn btn-sm btn-danger ms-2"
                  onClick={() => simpleCall('factoryReset', wipe ? 'true' : 'false')}
                >
                  {t('factoryReset')}
                </button>
              </div>
            )}
          </div>
        )}
        {device ? (
          <div className="mb-3">
            <DeviceSettingsCore
              onSaveThresholds={saveThresholdsOnly}
              canSaveThresholds={hasThresholdChanges}
              saving={savingSettings}
              controller={device.controller || 'climate'}
              t={t}
              telegramEnabled={telegramEnabled}
              setTelegramEnabled={setTelegramEnabled}
              powerAlerts={powerAlerts}
              setPowerAlerts={setPowerAlerts}
              thresholdAlerts={thresholdAlerts}
              setThresholdAlerts={setThresholdAlerts}
              trendAlerts={trendAlerts}
              setTrendAlerts={setTrendAlerts}
              thresholds={settingsThresholds}
              setThresholdField={setThresholdField}
              waterConfig={settingsWaterConfig}
              setWaterConfigField={setWaterConfigField}
              timeSyncPending={timeActionPending}
              co2OffsetDraft={co2OffsetDraft}
              setCo2OffsetDraft={setCo2OffsetDraft}
              calibrationTargetPpm={calibrationTargetPpm}
              setCalibrationTargetPpm={setCalibrationTargetPpm}
              onForceCalibrate={forceCalibrateFromCore}
              pendingCalibration={pendingCalibration}
              calibrationStartedUi={calibrationStartedUi && calibrationCountdownSec > 0}
              calibrationCountdownText={calibrationCountdownText}
              settingsStatus={settingsStatus}
              settingsProgressText={settingsProgressText}
              deviceName={deviceDisplayName}
              setDeviceName={setDeviceDisplayName}
              relayStatuses={latestRelayStatuses}
              relayModes={relayModes}
              onRelayCommand={handleRelayCommand}
              relayPending={relayPending}
              relayCommandStatus={relayCommandStatus}
              onSaveDisplayName={saveDisplayName}
              nameSavePending={nameSaving}
              canRestartController={canRestartController}
              onRestartController={restartController}
              restartPending={restartPending}
              restartStatus={restartStatus}
              notificationDelivery={notificationDelivery}
              notificationActionPending={notificationActionPending}
              onEnableClimateAlerts={enableClimateNotificationsForMe}
            />
          </div>
        ) : null}
        <div className="device-settings-modal__section mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h6 className="fw-bold mb-0">{tx('timeSyncTitle')}</h6>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              style={{ borderRadius: 12 }}
              onClick={refreshSystemTime}
              disabled={timeActionPending}
            >
              {tx('refreshTimeSync')}
            </button>
          </div>
          <div className="small text-muted mb-2">
            {tx('timeSyncHint')}
          </div>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="text-muted small">{tx('serverTimeLabel')}</div>
              <div className="fw-semibold">
                {formatDateTime(systemTime?.serverTime, locale, timezone)}
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">{tx('controllerTimeLabel')}</div>
              <div className="fw-semibold">
                {formatDateTime(controllerTime, locale, timezone)}
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-muted small">{tx('timeDriftLabel')}</div>
              <div>
                <span className={`badge bg-${driftTone}`}>
                  {driftSeconds == null
                    ? tx('timeUnknown')
                    : `${driftSeconds.toFixed(1)}s`}
                </span>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">{tx('farmTimezoneLabel')}</label>
            <div className="d-flex gap-2 flex-wrap">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 320 }}
                value={timezoneDraft}
                onChange={(e) => setTimezoneDraft(e.target.value)}
                placeholder={t('deviceSettingsPage.timezonePlaceholder')}
              />
              <button
                className="btn btn-outline-secondary"
                onClick={saveTimezone}
                disabled={timeActionPending}
              >
                {tx('saveTimezone')}
              </button>
            </div>
            <div className="form-text">
              {tx('farmTimezoneHint')}
            </div>
            <p className="small text-muted mt-2 mb-0">
              {tx('deviceSettingsPage.co2OffsetSaveHint')}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: 12 }}
              onClick={syncControllerTimeNow}
              disabled={timeActionPending}
            >
              {tx('syncNow')}
            </button>
          </div>
          <div>
            <label className="form-label">{tx('manualTimeLabel')}</label>
            <div className="d-flex gap-2 flex-wrap">
              <input
                type="datetime-local"
                className="form-control"
                style={{ maxWidth: 320, borderRadius: 12 }}
                value={manualTimestamp}
                onChange={(e) => setManualTimestamp(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                style={{ borderRadius: 12 }}
                onClick={setManualControllerTime}
                disabled={timeActionPending}
              >
                {tx('setManualTime')}
              </button>
            </div>
            <div className="form-text">
              {tx('manualTimeHint')}
            </div>
          </div>
        </div>
        <div className="device-settings-modal__section mb-0">
          <h6 className="fw-bold mb-2">{t('deviceSettingsPage.advancedFunctionsTitle')}</h6>
          {!vpdAnalysisAllowed && availableFunctions.includes('setVPDType') ? (
            <div className="alert alert-secondary small py-2 mb-2" role="status">
              {t('deviceSettingsPage.vpdProPlanLock')}
            </div>
          ) : null}
          <select
            className="form-select w-auto mb-2"
            style={{ borderRadius: 12 }}
            value={active === 'setVPDType' && !vpdAnalysisAllowed ? '' : active}
            onChange={(e) => setActive(e.target.value)}
          >
            <option value="">{t('selectFunction')}</option>
            {availableFunctions.map((fn) => {
              const def = defFor(fn);
              const planLocked = fn === 'setVPDType' && !vpdAnalysisAllowed;
              return (
                <option key={fn} value={fn} disabled={disabled[fn] || planLocked}>
                  {planLocked ? `${t(`functions.${fn}`)} — ${t('deviceSettingsPage.locked')}` : t(`functions.${fn}`)}
                </option>
              );
            })}
          </select>
          {active && !(active === 'setVPDType' && !vpdAnalysisAllowed) && renderForm(active)}
        </div>
      </div>
    </div>
  );
}
